// The scan selection is mirrored in the URL query, so a reload — or a hand-off from the
// landing — restores exactly the modes that were picked. One param per mode, written only
// while it is on; an absent param means off. Both directions live here so the reader and
// the writer can't drift apart (they did: grammar was readable but never written, so
// picking it and reloading came back as a deep scan).

import type {Plan} from "types";
import type {SignupReason} from "hooks/useAuth";
import {PLANS_ORDER, HUMANIZER_MODES, type HumanizerMode} from "config";

/** The four scan modes as a complete selection — what the store holds and the URL carries. */
export type ScanModeFlags = {ai: boolean; deep: boolean; plagiarism: boolean; grammar: boolean};

const PARAM: Record<keyof ScanModeFlags, string> = {
    ai: 'aiScan',
    deep: 'deepAiScan',
    plagiarism: 'plagiarism',
    grammar: 'grammar',
};

const MODES = Object.keys(PARAM) as (keyof ScanModeFlags)[];

/** The Humanizer folder, a pick of its own beside the four scans (the phone's menu lists it
 *  as one more row). Written alone: nothing scans while it is open. */
const HUMANIZE = 'humanize';

/** Whether the query opens the Humanizer. */
export const readHumanize = (search: string): boolean =>
    new URLSearchParams(search).get(HUMANIZE) === 'true';

/** A landing choice only applies to a Humanizer link. Missing/invalid values leave
 * the current mode alone, including a draft restored after login. */
export const readHumanizerMode = (search: string): HumanizerMode | null => {
    if (!readHumanize(search)) return null;
    const mode = new URLSearchParams(search).get('humanizer_mode');
    return HUMANIZER_MODES.includes(mode as HumanizerMode) ? mode as HumanizerMode : null;
};

/**
 * Read the scan selection out of a URL query string, or null when the query names no scan
 * param at all — then there is nothing to restore and the current selection stands.
 *
 * Every mode is read on its own: the tab and the AI depth travel together (an add-on's tab
 * plus deep/simple for the AI tab), so reading must mirror what writeScanModes emits. The one
 * collapse is deep over the simple AI scan, which the UI keeps mutually exclusive.
 */
export const readScanModes = (search: string): ScanModeFlags | null => {
    const params = new URLSearchParams(search);
    if (!MODES.some(mode => params.has(PARAM[mode]))) return null;

    const on = (mode: keyof ScanModeFlags) => params.get(PARAM[mode]) === 'true';
    const deep = on('deep');
    return {ai: !deep && on('ai'), deep, plagiarism: on('plagiarism'), grammar: on('grammar')};
};

/** Mirror the page back into `href`: the Humanizer open, or each mode that is on with its
 *  param and each mode that is off without. Unrelated params (utm, tokens, …) are left untouched. */
export const writeScanModes = (href: string, modes: ScanModeFlags,
    {humanizer = false, humanizerMode}: {humanizer?: boolean; humanizerMode?: HumanizerMode} = {}): string => {
    const url = new URL(href);
    MODES.forEach(mode => {
        if (modes[mode] && !humanizer) url.searchParams.set(PARAM[mode], 'true');
        else url.searchParams.delete(PARAM[mode]);
    });
    if (humanizer) url.searchParams.set(HUMANIZE, 'true');
    else url.searchParams.delete(HUMANIZE);
    if (humanizer && humanizerMode) url.searchParams.set('humanizer_mode', humanizerMode);
    else if (!humanizer) url.searchParams.delete('humanizer_mode');
    return url.toString();
};

/** What a hand-off to the app asks beyond its text or files (utils/appHandoff): whether the
 *  page presses Run scan for the user, and what it opens — the sign-up, or a plan's checkout. */
export type HandoffLaunch = {autorun: boolean; signup: SignupReason | null; upgrade: Plan | null};
/** What the app opens with after a hand-off: its sign-up, or this plan's checkout. */
export type HandoffThen = 'signup' | Plan;

export const HANDOFF_PARAMS = ['autorun', 'signup', 'upgrade'];
// ?signup=true is the general sign-up (external "Sign up now" links), =words the one selling the free scan words.
const SIGNUP_REASONS = new Map<string, SignupReason>([['true', 'general'], ['words', 'words']]);
const PURCHASABLE = PLANS_ORDER.filter(plan => plan !== 'free' && plan !== 'enterprise');

export const readHandoffLaunch = (search: string): HandoffLaunch => {
    const params = new URLSearchParams(search);
    const upgrade = params.get('upgrade') as Plan | null;
    return {
        autorun: params.get('autorun') !== 'false',
        signup: SIGNUP_REASONS.get(params.get('signup') ?? '') ?? null,
        upgrade: upgrade && PURCHASABLE.includes(upgrade) ? upgrade : null,
    };
};

/** A hand-off leaves the Run scan to the user; `then` is what the app opens. */
export const writeHandoffLaunch = (url: URL, then?: HandoffThen) => {
    url.searchParams.set('autorun', 'false');
    if (then === 'signup') url.searchParams.set('signup', 'words');
    else if (then) url.searchParams.set('upgrade', then);
};
